import type { NewUserQuizPriority } from '@/shared/api/newUserQuiz';

import type { NewUserQuizCopy } from './i18n';
import type { NewUserQuizFormState, QuizQuestion } from './types';

export const NEW_USER_QUIZ_AUTO_ADVANCE_MS = 300;
export const NEW_USER_QUIZ_BYPASS_TTL_MS = 24 * 60 * 60 * 1000;
export const NEW_USER_QUIZ_MAX_SUBMIT_ATTEMPTS = 3;
export const NEW_USER_QUIZ_BYPASS_STORAGE_KEY = 'newUserQuiz.bypassedAt';

export const NEW_USER_QUIZ_INITIAL_STATE: NewUserQuizFormState = {
  content_team_size: null,
  localization_frequency: null,
  localized_videos_before: null,
  priorities: [],
  target_language_count: null,
};

export const getNewUserQuizQuestions = (copy: NewUserQuizCopy): readonly QuizQuestion[] => [
  {
    ...copy.questions.localized_videos_before,
    desktopPaddingTop: 180,
    desktopWidth: 424,
    key: 'localized_videos_before',
    options: [
      { ...copy.questions.localized_videos_before.options.true, value: true },
      { ...copy.questions.localized_videos_before.options.false, value: false },
    ],
  },
  {
    ...copy.questions.content_team_size,
    desktopPaddingTop: 180,
    desktopWidth: 644,
    key: 'content_team_size',
    options: [
      { ...copy.questions.content_team_size.options.just_me, value: 'just_me' },
      { ...copy.questions.content_team_size.options.small_team_2_5, value: 'small_team_2_5' },
      { ...copy.questions.content_team_size.options.large_team_5_plus, value: 'large_team_5_plus' },
    ],
  },
  {
    ...copy.questions.localization_frequency,
    desktopPaddingTop: 180,
    desktopWidth: 864,
    key: 'localization_frequency',
    options: [
      { ...copy.questions.localization_frequency.options.one_time_project, value: 'one_time_project' },
      { ...copy.questions.localization_frequency.options.occasionally_quarterly, value: 'occasionally_quarterly' },
      { ...copy.questions.localization_frequency.options.regularly_monthly, value: 'regularly_monthly' },
      { ...copy.questions.localization_frequency.options.continuously, value: 'continuously' },
    ],
  },
  {
    ...copy.questions.target_language_count,
    desktopPaddingTop: 120,
    desktopWidth: 864,
    key: 'target_language_count',
    options: [
      { ...copy.questions.target_language_count.options.one, value: 'one' },
      { ...copy.questions.target_language_count.options.two_to_three, value: 'two_to_three' },
      { ...copy.questions.target_language_count.options.four_to_ten, value: 'four_to_ten' },
      { ...copy.questions.target_language_count.options.ten_plus, value: 'ten_plus' },
      { ...copy.questions.target_language_count.options.changes_by_project, value: 'changes_by_project' },
    ],
  },
];

export const getNewUserQuizPriorityOptions = (
  copy: NewUserQuizCopy,
): readonly {
  label: string;
  value: NewUserQuizPriority;
}[] => [
  { ...copy.priorityQuestion.options.fast_results_no_setup, value: 'fast_results_no_setup' },
  { ...copy.priorityQuestion.options.consistent_voice_and_terminology, value: 'consistent_voice_and_terminology' },
  {
    ...copy.priorityQuestion.options.collaborate_with_team_or_reviewers,
    value: 'collaborate_with_team_or_reviewers',
  },
  {
    ...copy.priorityQuestion.options.integration_into_product_or_workflow,
    value: 'integration_into_product_or_workflow',
  },
  { ...copy.priorityQuestion.options.not_sure_yet, value: 'not_sure_yet' },
];

export const NEW_USER_QUIZ_TOTAL_STEPS = 5;

export const NEW_USER_QUIZ_TEST_IDS = {
  back: 'new-user-quiz-back',
  booking: 'new-user-quiz-booking',
  continue: 'new-user-quiz-continue',
  continueAnyway: 'new-user-quiz-continue-anyway',
  error: 'new-user-quiz-error',
  maybeLater: 'new-user-quiz-maybe-later',
  option: (value: boolean | string) => `new-user-quiz-option-${String(value)}`,
  quiz: 'new-user-quiz',
} as const;
